import { useSelector } from "react-redux";
import { sha256 } from 'js-sha256';
import WidgetCards from "./WidgetCards";
import WidgetCardsLandscape from "./WidgetCardsLandscape";
import { TicketWidgetRefreshTime } from "../../../../Constants";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { GetLastRefreshDateTime, GetWidgetData, GetWidgetSettings, GridMetaData_Format } from "../../../../Helper/Services";
import { GetFormattedGridData, IsSharedFilterAlert } from "../../../../Helper/WidgetDataCommon";
import { UpdatedWidgetData } from "../TableWidget/TableWidgetService";

const areTicketRowsEqual = (prevRows = [], nextRows = []) => {
    if (prevRows === nextRows) return true;
    if (!Array.isArray(prevRows) || !Array.isArray(nextRows)) return false;
    if (prevRows.length !== nextRows.length) return false;

    for (let index = 0; index < prevRows.length; index++) {
        const prev = prevRows[index] || {};
        const next = nextRows[index] || {};

        if (
            prev.id !== next.id ||
            prev.title !== next.title ||
            prev.description !== next.description ||
            prev.owner !== next.owner ||
            prev.percent !== next.percent ||
            prev.topLeftText !== next.topLeftText ||
            prev.imageUrl !== next.imageUrl ||
            prev.openDate !== next.openDate ||
            prev.promiseDate !== next.promiseDate ||
            prev.status !== next.status ||
            prev.headerText !== next.headerText ||
            prev.targetHours !== next.targetHours ||
            prev.actualHours !== next.actualHours ||
            prev.completedDate !== next.completedDate ||
            prev.description1 !== next.description1
        ) {
            return false;
        }
    }

    return true;
};

const TicketWidget = (props) => {
    const { widgetId, onGetNotification, RefreshVersion } = props;
    const sessionId = useSelector((state) => state.login.sessionId);
    const isOtherUserPage = useSelector((state) => state.layout.IsOtherUserPage);
    const sharedUserEmail = useSelector((state) => state.layout.SharedUserEmail);
    const currentTheme = useSelector((state) => state.layout.theme);
    const isDarkTheme = String(currentTheme || "").toLowerCase() === "dark";
    const [widgetDt, setWidgetDt] = useState([]);
    const [screenMsg, setScreenMsg] = useState("No Data");
    const [showNoData, setShowNoData] = useState(false);
    const [minOpenTicketCount, setMinOpenTicketCount] = useState(1);
    const [autoRevertDurationinSec, setAutoRevertDurationinSec] = useState(0);
    const [openTicketsPosition, setOpenTicketsPosition] = useState("");
    const [isLandscapeMode, setIsLandscapeMode] = useState(false);
    const [widgetViewportWidth, setWidgetViewportWidth] = useState(0);
    const [widgetViewportHeight, setWidgetViewportHeight] = useState(0);
    const requestVersionRef = useRef(0);
    const onGetNotificationRef = useRef(onGetNotification);
    const lastNotificationValueRef = useRef(undefined);
    const isFetchingRef = useRef(false);
    const widgetContainerRef = useRef(null);
    const isLandscapeModeRef = useRef(false);

    useEffect(() => {
        onGetNotificationRef.current = onGetNotification;
    }, [onGetNotification]);

    useLayoutEffect(() => {
        if (!widgetContainerRef.current) return;

        const updateOrientation = () => {
            const currentHeight = widgetContainerRef.current?.clientHeight || 0;
            const currentWidth = widgetContainerRef.current?.clientWidth || 0;
            const quantizedHeight = Math.max(0, Math.round(currentHeight / 8) * 8);
            const quantizedWidth = Math.max(0, Math.round(currentWidth / 8) * 8);
            // Hysteresis: enter landscape at ≥1.6x ratio, leave landscape below 1.45x.
            // Prevents rapid portrait↔landscape remounts when the widget sits near the boundary.
            const ratio = quantizedHeight > 0 ? quantizedWidth / quantizedHeight : 0;
            const currentlyLandscape = isLandscapeModeRef.current;
            const nextLandscapeMode = currentlyLandscape
                ? ratio > 1 && ratio >= 1.45
                : ratio > 1 && ratio >= 1.6;
            isLandscapeModeRef.current = nextLandscapeMode;
            setWidgetViewportWidth((prev) => (prev === quantizedWidth ? prev : quantizedWidth));
            setWidgetViewportHeight((prev) => (prev === quantizedHeight ? prev : quantizedHeight));
            setIsLandscapeMode((prev) => (prev === nextLandscapeMode ? prev : nextLandscapeMode));
        };

        updateOrientation();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(() => updateOrientation());
            observer.observe(widgetContainerRef.current);
            return () => observer.disconnect();
        }

        window.addEventListener("resize", updateOrientation);
        return () => window.removeEventListener("resize", updateOrientation);
    }, []);

    useEffect(() => {
        let isDisposed = false;

        const getWidgetSetting = async () => {
            if (isFetchingRef.current || sessionId === "" || sessionId == null) return;

            const currentRequestVersion = ++requestVersionRef.current;
            isFetchingRef.current = true;
            let isSharedFilter = false;
            let sharedFilterUserId = "";
            let filterId = "";

            try {
                const widgetSettings = await GetWidgetSettings(widgetId, sessionId);
                if (isDisposed || currentRequestVersion !== requestVersionRef.current) return;

                if (
                    widgetSettings["IsSharedFilter"] !== undefined &&
                    widgetSettings["IsSharedFilter"] === "true"
                ) {
                    isSharedFilter = true;
                    sharedFilterUserId = widgetSettings["OriginalUserId"];
                    filterId = widgetSettings["FilterId"];
                }

                setMinOpenTicketCount(widgetSettings["numberOfTickets"] != null ? parseInt(widgetSettings["numberOfTickets"]) : 0);
                const autoRevertValue = widgetSettings["preserveTicketViewChanges"];
                const parsedAutoRevert = Number.parseInt(autoRevertValue, 10);
                setAutoRevertDurationinSec(Number.isFinite(parsedAutoRevert) ? parsedAutoRevert : 0);
                setOpenTicketsPosition(widgetSettings["openTicketsPosition"] ? widgetSettings["openTicketsPosition"] : "bottom");

                const widgetDataResponse = await GetWidgetData(
                    widgetSettings["Filter"],
                    widgetSettings["reports"],
                    widgetSettings["connection"],
                    sessionId,
                    isOtherUserPage,
                    sharedUserEmail,
                    isSharedFilter,
                    sharedFilterUserId,
                    filterId
                );
                if (isDisposed || currentRequestVersion !== requestVersionRef.current) return;

                const lstReponseDt = await GetLastRefreshDateTime(widgetSettings["reports"], widgetSettings["connection"], sessionId);
                if (isDisposed || currentRequestVersion !== requestVersionRef.current) return;

                if (lstReponseDt !== lastNotificationValueRef.current) {
                    lastNotificationValueRef.current = lstReponseDt;
                    onGetNotificationRef.current?.(lstReponseDt);
                }

                const metaData = GridMetaData_Format(widgetDataResponse);
                if (IsSharedFilterAlert(widgetDataResponse)) {
                    setScreenMsg(widgetDataResponse);
                    setShowNoData(true);
                    return;
                }

                const getFormattedDt = GetFormattedGridData(widgetDataResponse);

                //apply sorting based on selected sorting by option in widget setting
                //check if sorting by column is present in the data and then apply sorting
                //also check if sorting by column is not empty in widget setting
                //also check if column data should be srted based on type of data in the column like date, number or string
                if (widgetSettings["sortingBy"] !== undefined && widgetSettings["sortingBy"] !== "") {
                    const sortDirectionVal = widgetSettings["sortDirection"] || 'asc';
                    const sortDirection = sortDirectionVal === "asc" ? 1 : -1;
                    getFormattedDt.sort((a, b) => {
                        const aVal = a[widgetSettings["sortingBy"]];
                        const bVal = b[widgetSettings["sortingBy"]];

                        // Handle null/undefined values
                        if (aVal == null && bVal == null) return 0;
                        if (aVal == null) return 1 * sortDirection;
                        if (bVal == null) return -1 * sortDirection;

                        // Detect data type
                        const aIsDate = !isNaN(Date.parse(aVal)) && typeof aVal === 'string' && aVal.trim() !== '';
                        const bIsDate = !isNaN(Date.parse(bVal)) && typeof bVal === 'string' && bVal.trim() !== '';
                        const aIsNum = !isNaN(aVal) && aVal !== '';
                        const bIsNum = !isNaN(bVal) && bVal !== '';

                        // Sort by type: dates, then numbers, then strings
                        if (aIsDate && bIsDate) {
                            return (new Date(aVal) - new Date(bVal)) * sortDirection;
                        } else if (aIsDate) {
                            return -1 * sortDirection;
                        } else if (bIsDate) {
                            return 1 * sortDirection;
                        }

                        if (aIsNum && bIsNum) {
                            return (parseFloat(aVal) - parseFloat(bVal)) * sortDirection;
                        } else if (aIsNum) {
                            return -1 * sortDirection;
                        } else if (bIsNum) {
                            return 1 * sortDirection;
                        }

                        // String comparison
                        if (String(aVal) < String(bVal)) {
                            return -1 * sortDirection;
                        }
                        if (String(aVal) > String(bVal)) {
                            return 1 * sortDirection;
                        }
                        return 0;
                    });
                }

                const visibleFormattedDt = getFormattedDt;//.slice(0, maxTicketsToDisplay);

                const getDateTimeConvertedDt = UpdatedWidgetData(
                    metaData,
                    visibleFormattedDt,
                    widgetSettings["reports"]
                ).map((item, index) => {

                    return {
                        id: index,
                        title: item[widgetSettings["titleText"]],
                        description: item[widgetSettings["subTitleText"]],
                        description1: item[widgetSettings["subTitle1Text"]],
                        owner: item[widgetSettings["username"]],
                        percent: widgetSettings["percentageOfTarget1"] ? `${Math.round(item[widgetSettings["percentageOfTarget1"]] * 100)}%` : "0%",
                        topLeftText: item[widgetSettings["topLeftLabel"]],
                        imageUrl: getGravatarURL(item[widgetSettings["imagePath"]]),
                        openDate: handleNullDates(widgetSettings["openedText"] ? item[widgetSettings["openedText"]] : null),
                        promiseDate: handleNullDates(widgetSettings["promisedText"] ? item[widgetSettings["promisedText"]] : null),
                        status: item['Status'] ? item['Status'] : "NA",
                        headerText: item[widgetSettings["headerText"]],
                        targetHours: item[widgetSettings["topLeftLabel"]] ?? null,
                        actualHours: widgetSettings["actualHoursLabel"] ? (item[widgetSettings["actualHoursLabel"]] ?? null) : null,
                        completedDate: handleNullDates(widgetSettings["completedText"] ? item[widgetSettings["completedText"]] : null),
                    };
                });//.splice(0, 3); // Limit to top 5 tickets

                console.log('getDateTimeConvertedDt', getDateTimeConvertedDt)

                if (isDisposed || currentRequestVersion !== requestVersionRef.current) return;
                setWidgetDt((prev) => (areTicketRowsEqual(prev, getDateTimeConvertedDt) ? prev : getDateTimeConvertedDt));
                setShowNoData(getDateTimeConvertedDt.length === 0);
            } catch (err) {
                console.error("TicketWidget refresh failed:", err);
            } finally {
                isFetchingRef.current = false;
            }
        };

        getWidgetSetting();

        const handleNullDates = (nDate) => {
            if (nDate === "" || nDate === "NULL") {
                return "NA";
            }
            var getDate = new Date(nDate);
            var checkDate = new Date(1970, 12, 30);
            if (checkDate > getDate) {
                return "NA";
            }

            if (isNaN(Date.parse(nDate))) {
                return "NA";
            }
            return new Date(nDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
        };

        const getGravatarURL = (email) => {

            //check string is email or not using regex
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                console.warn("Invalid email address for gravatar:", email);
                return "";
            }

            // Trim leading and trailing whitespace from
            // an email address and force all characters
            // to lower case
            const address = String(email).trim().toLowerCase();

            // Create a SHA256 hash of the final string
            const hash = sha256(address);

            // Grab the actual image URL
            return `https://gravatar.com/avatar/${hash}`;
        }

        const refreshWidget = setInterval(() => {
            console.log("set Timout..", new Date().toLocaleTimeString());
            getWidgetSetting();
        }, TicketWidgetRefreshTime);

        return () => {
            isDisposed = true;
            requestVersionRef.current += 1;
            isFetchingRef.current = false;
            //clear setinterval on unmount component
            clearInterval(refreshWidget);
        };
    }, [sessionId, RefreshVersion, widgetId, isOtherUserPage, sharedUserEmail]);

    const commonCardProps = {
        cardsData: widgetDt,
        minOpenTicketCount,
        autoRevertDurationinSec,
        isDarkTheme,
        OpenTicketPlacement: openTicketsPosition,
    };

    return <div ref={widgetContainerRef} style={{ width: "100%", height: "100%", position: "relative" }}>
        {isLandscapeMode ? (
            <WidgetCardsLandscape
                {...commonCardProps}
                widgetViewportWidth={widgetViewportWidth}
                widgetViewportHeight={widgetViewportHeight}
            />
        ) : (
            <WidgetCards {...commonCardProps} />
        )}
        {widgetDt.length === 0 && showNoData === true && (
            <div
                style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "rgb(153, 153, 153)",
                    fontSize: "17px",
                }}
            >
                {screenMsg}
            </div>
        )}
    </div>;
}

export default TicketWidget;